import { ParkingArea, ParkingSpot } from '../types/parking';
import { BaseComponent, BuildingStructure, PhotoModel, RoomModel } from '../types/protocols';
import { BaseType, ParkingAreaKind, ParkingBlueprintSource, ParkingSpotType, RoomType, UtilityCategory } from '../types/Enums';
import { translateParkingAreaKind } from '../types/EnumTranslations';
import { naturalCompare } from './naturalSort';
import { nextNumberInSeries } from './numberSeries';
import carIcon from '../Images/parking/car.png';
import motorcycleIcon from '../Images/parking/motorcycle.png';
import accessibleIcon from '../Images/parking/accessible.png';
import oversizedIcon from '../Images/parking/oversized.png';
import { RENTED_COLOR, VACANT_COLOR } from './leaseStatusColors';

/** Type icon per spot type: a black glyph on transparent, drawn in any colour by `MaskedIcon`. */
const SPOT_TYPE_ICONS: Record<ParkingSpotType, string> = {
    [ParkingSpotType.Car]: carIcon,
    [ParkingSpotType.Motorcycle]: motorcycleIcon,
    [ParkingSpotType.Accessible]: accessibleIcon,
    [ParkingSpotType.Oversized]: oversizedIcon,
};

/** Icon URL for a spot type; an unknown type falls back to the car. */
export const spotTypeIcon = (type: ParkingSpotType): string =>
    SPOT_TYPE_ICONS[type] ?? SPOT_TYPE_ICONS[ParkingSpotType.Car];

/** Status colour of a spot: rented (`currentLeaseKey` set, so an upcoming-only lease too) is grey, else green. */
export const spotStatusColor = (spot: Pick<ParkingSpot, 'currentLeaseKey'>): string =>
    isSpotRented(spot) ? RENTED_COLOR : VACANT_COLOR;

/** The area's name, else its kind ("Utomhusparkering"). */
export const parkingAreaDisplayName = (area: ParkingArea): string => {
    const trimmed = (area.name || '').trim();
    return trimmed.length > 0 ? trimmed : translateParkingAreaKind(area.kind);
};

export const parkingSpotDisplayName = (spot: ParkingSpot): string => {
    const trimmed = (spot.number || '').trim();
    return trimmed.length > 0 ? `Plats ${trimmed}` : 'Plats utan nummer';
};

// MARK: - Drawing resolution

/** Why an area has no drawing; UI copy in AREA_BLUEPRINT_STATUS_TEXT. */
export type AreaBlueprintProblem = 'roomMissing' | 'roomHasNoLevel' | 'levelMissing' | 'levelHasNoDrawing' | 'noOwnDrawing';

export type AreaBlueprintResolution =
    | {
        status: 'ok';
        /** Image key the spots' blueprintLocations are keyed by. */
        key: string;
        image: PhotoModel;
        /** `level`: the level's drawing (never tombstone it from the area). */
        owner: 'area' | 'level';
        /** Level the drawing belongs to; set when owner is `level`. */
        levelKey?: string;
    }
    | { status: AreaBlueprintProblem };

/** Shared UI copy per resolver status. */
export const AREA_BLUEPRINT_STATUS_TEXT: Record<AreaBlueprintProblem, string> = {
    roomMissing: 'Rummet finns inte längre',
    roomHasNoLevel: 'Rummet saknar våning',
    levelMissing: 'Våningen finns inte längre',
    levelHasNoDrawing: 'Våningen saknar ritning',
    noOwnDrawing: 'Ingen ritning',
};

type ResolvableArea = Pick<ParkingArea, 'roomKey' | 'blueprintSource' | 'blueprints' | 'currentBlueprintKey'>;

export const isLevelBlueprintSource = (area: Pick<ParkingArea, 'blueprintSource'>): boolean =>
    area.blueprintSource === ParkingBlueprintSource.Level;

/**
 * The area's drawing, resolved live. Level mode: roomKey → room.levelKey →
 * level.currentBlueprintKey → level.blueprints[key]. Own mode (default):
 * area.currentBlueprintKey → area.blueprints[key]. Never throws on broken
 * references; every reader of an area's drawing goes through this.
 * Same name, inputs and statuses as iOS/Android.
 */
export const resolveAreaBlueprint = (
    area: ResolvableArea,
    structure: BuildingStructure | undefined | null
): AreaBlueprintResolution => {
    if (!isLevelBlueprintSource(area)) {
        const key = area.currentBlueprintKey;
        const image = key ? area.blueprints?.[key] : undefined;
        return key && image ? { status: 'ok', key, image, owner: 'area' } : { status: 'noOwnDrawing' };
    }

    const room = area.roomKey ? structure?.rooms?.[area.roomKey] : undefined;
    if (!room) return { status: 'roomMissing' };
    const levelKey = typeof room.levelKey === 'string' ? room.levelKey.trim() : '';
    if (!levelKey) return { status: 'roomHasNoLevel' };
    const level = structure?.levels?.[levelKey];
    if (!level) return { status: 'levelMissing' };
    const key = level.currentBlueprintKey;
    const image = key ? level.blueprints?.[key] : undefined;
    if (!key || !image) return { status: 'levelHasNoDrawing' };
    return { status: 'ok', key, image, owner: 'level', levelKey };
};

/** The resolved drawing key, or undefined when the area shows no drawing. */
export const resolvedAreaBlueprintKey = (
    area: ResolvableArea,
    structure: BuildingStructure | undefined | null
): string | undefined => {
    const resolution = resolveAreaBlueprint(area, structure);
    return resolution.status === 'ok' ? resolution.key : undefined;
};

/** Spots with a marker on the given drawing key. */
export const spotsPlacedOnDrawing = (area: Pick<ParkingArea, 'spots'>, imageKey: string | undefined): number => {
    if (!imageKey) return 0;
    return Object.values(area.spots || {}).filter(spot => !!spot.blueprintLocations?.[imageKey]).length;
};

/**
 * Number of spots that would have to be placed again when the area goes
 * from `before` to `after`. Zero when the resolved drawing key does not change
 * or nothing is placed on the current drawing; a non-zero result requires a
 * confirmation ("N platser är placerade i nuvarande ritning …").
 */
export const spotsHiddenByDrawingChange = (
    before: ResolvableArea & Pick<ParkingArea, 'spots'>,
    after: ResolvableArea,
    structure: BuildingStructure | undefined | null
): number => {
    const beforeKey = resolvedAreaBlueprintKey(before, structure);
    const afterKey = resolvedAreaBlueprintKey(after, structure);
    if (beforeKey === afterKey) return 0;
    return spotsPlacedOnDrawing(before, beforeKey);
};

/** Confirmation copy when a drawing change hides placed spots. */
export const drawingChangeConfirmationText = (count: number): string =>
    `${count} ${count === 1 ? 'plats är placerad' : 'platser är placerade'} i nuvarande ritning och behöver placeras ut på nytt.`;

/** Level-mode areas whose drawing resolves to this level. Own-mode areas are never included. */
export const areasOnLevel = (
    areas: { [areaKey: string]: ParkingArea } | ParkingArea[],
    structure: BuildingStructure | undefined | null,
    levelKey: string
): ParkingArea[] => {
    const list = Array.isArray(areas) ? areas : Object.values(areas);
    return list.filter(area => {
        if (!isLevelBlueprintSource(area)) return false;
        const resolution = resolveAreaBlueprint(area, structure);
        return resolution.status === 'ok' && resolution.levelKey === levelKey;
    });
};

/**
 * Rooms on a level for the area room picker: `room_garage` first,
 * then the rest; within each group by number (natural), label, key.
 */
export const roomsOnLevel = (structure: BuildingStructure | undefined | null, levelKey: string): RoomModel[] => {
    if (!levelKey) return [];
    return Object.entries(structure?.rooms || {})
        .filter(([, room]) => room && room.levelKey === levelKey)
        .map(([key, room]) => (room.key ? room : { ...room, key }))
        .sort((a, b) => {
            const aGarage = a.type === RoomType.RoomGarage ? 0 : 1;
            const bGarage = b.type === RoomType.RoomGarage ? 0 : 1;
            if (aGarage !== bGarage) return aGarage - bGarage;
            const byNumber = naturalCompare(a.number || '￿', b.number || '￿');
            if (byNumber !== 0) return byNumber;
            const byLabel = naturalCompare(a.label || '', b.label || '');
            return byLabel !== 0 ? byLabel : a.key.localeCompare(b.key);
        });
};

/** Spots of an area in natural number order (2 < 10, P3 < P12), key as tie-breaker. */
export const sortedParkingSpots = (area: ParkingArea): ParkingSpot[] => {
    return Object.values(area.spots || {}).sort((a, b) => {
        const byNumber = naturalCompare(a.number || '', b.number || '');
        return byNumber !== 0 ? byNumber : a.key.localeCompare(b.key);
    });
};

export const spotHasCharger = (spot: ParkingSpot): boolean => {
    return typeof spot.chargerComponentKey === 'string' && spot.chargerComponentKey.length > 0;
};

/** Rented state is the explicit `currentLeaseKey` written by leasesApi; no date logic here. */
export const isSpotRented = (spot: Pick<ParkingSpot, 'currentLeaseKey'>): boolean => {
    return typeof spot.currentLeaseKey === 'string' && spot.currentLeaseKey.length > 0;
};

export const vacantSpotCount = (area: Pick<ParkingArea, 'spots'>): number => {
    return Object.values(area.spots || {}).filter(spot => !isSpotRented(spot)).length;
};

/**
 * Next number in a series: "12" → "13", "P09" → "P10". Shared helper in
 * Helpers/numberSeries (also used by storage units); mirrors iOS.
 */
export const nextSpotNumber = (previous: string): string | null => nextNumberInSeries(previous);

// MARK: - Charger picker

type ChargerCandidateComponent = Pick<BaseComponent, 'type' | 'utilityCategory' | 'archivedAt' | 'parentRoomKeys'> & { key?: string };
type ChargerScopeArea = Pick<ParkingArea, 'kind' | 'roomKey'>;

/** Chargers are utility components in the electrical category. */
export const isElectricalUtility = (component: Pick<BaseComponent, 'type' | 'utilityCategory'>): boolean =>
    component.type === BaseType.Utility && component.utilityCategory === UtilityCategory.Electrical;

/** True when a garage area points at a room that is no longer in the structure. */
export const areaRoomMissing = (area: ChargerScopeArea, structure: BuildingStructure | undefined | null): boolean =>
    area.kind === ParkingAreaKind.Garage && !!area.roomKey && !structure?.rooms?.[area.roomKey];

const parentRoomKeysOf = (component: Pick<BaseComponent, 'parentRoomKeys'>): string[] => {
    const value = component.parentRoomKeys?.value;
    return Array.isArray(value) ? value.filter(k => typeof k === 'string' && k.length > 0) : [];
};

/** The garage's room key when that room still exists. */
const existingAreaRoomKey = (area: ChargerScopeArea, structure: BuildingStructure | undefined | null): string | undefined =>
    area.kind === ParkingAreaKind.Garage && area.roomKey && structure?.rooms?.[area.roomKey] ? area.roomKey : undefined;

/**
 * Predicate for the spot's charger picker. For a garage whose room still
 * exists: non-archived electrical utilities in any room on that room's level
 * (only that room when it has no level). Otherwise (no room, not a garage,
 * room deleted): electrical utilities without any parent room. The spot's
 * current charger always passes unless archived (the picker never lists
 * archived components).
 */
export const chargerCandidatePredicate = (
    area: ChargerScopeArea,
    structure: BuildingStructure | undefined | null,
    currentChargerKey?: string
) => {
    const roomKey = existingAreaRoomKey(area, structure);
    const levelKey = roomKey ? structure?.rooms?.[roomKey]?.levelKey : undefined;
    return (component: ChargerCandidateComponent, key: string | undefined = component.key): boolean => {
        if (component.archivedAt) return false;
        if (currentChargerKey && key === currentChargerKey) return true;
        if (!isElectricalUtility(component)) return false;
        const rooms = parentRoomKeysOf(component);
        if (!roomKey) return rooms.length === 0;
        if (!levelKey) return rooms.includes(roomKey);
        return rooms.some(k => structure?.rooms?.[k]?.levelKey === levelKey);
    };
};

/**
 * Where a charger candidate sits relative to the garage's room: undefined when
 * it is in that room (or the area has no existing room, so there is nothing to
 * compare with); otherwise the key of the other room it belongs to, preferring
 * one on the garage's level, or '' when it has no room at all.
 */
export const chargerOtherRoomKey = (
    area: ChargerScopeArea,
    structure: BuildingStructure | undefined | null,
    component: Pick<BaseComponent, 'parentRoomKeys'>
): string | undefined => {
    const roomKey = existingAreaRoomKey(area, structure);
    if (!roomKey) return undefined;
    const rooms = parentRoomKeysOf(component);
    if (rooms.includes(roomKey)) return undefined;
    const levelKey = structure?.rooms?.[roomKey]?.levelKey;
    return rooms.find(k => !!levelKey && structure?.rooms?.[k]?.levelKey === levelKey) ?? rooms[0] ?? '';
};

/** Picker order: the garage's room before other rooms, unlinked before linked. Lower first. */
export const chargerSortRank = (inOtherRoom: boolean, linked: boolean): number =>
    (inOtherRoom ? 2 : 0) + (linked ? 1 : 0);

/** Keys of the components the charger picker lists for this area. */
export const chargerCandidateKeys = (
    structure: BuildingStructure | undefined | null,
    area: ChargerScopeArea,
    currentChargerKey?: string
): string[] => {
    const isCandidate = chargerCandidatePredicate(area, structure, currentChargerKey);
    return Object.entries(structure?.components || {})
        .filter(([key, component]) => !!component && !!component.type && isCandidate(component, key))
        .map(([key]) => key);
};

// MARK: - Utilities on the area's drawing

/** Keys of the chargers linked by any spot in the area. */
export const linkedChargerKeys = (area: Pick<ParkingArea, 'spots'>): Set<string> =>
    new Set(
        Object.values(area.spots || {})
            .map(spot => spot?.chargerComponentKey)
            .filter((key): key is string => typeof key === 'string' && key.length > 0)
    );

/**
 * Utilities drawn on a level-mode area's drawing, read-only. The area shares
 * the level's image key, so a utility in a room on that level already carries
 * its pin under `blueprintLocations/{imageKey}`; nothing is written. Chargers
 * linked by a spot in the area are always included; the other utilities on the
 * level only when `showAll`. Archived components are left out, as in the
 * charger picker. Own-mode areas have no utilities on their drawing (plan A).
 */
export const utilitiesOnAreaDrawing = (
    structure: BuildingStructure | undefined | null,
    area: Pick<ParkingArea, 'spots'>,
    resolution: AreaBlueprintResolution | undefined,
    showAll: boolean
): BaseComponent[] => {
    if (!resolution || resolution.status !== 'ok' || resolution.owner !== 'level' || !resolution.levelKey) return [];
    const imageKey = resolution.key;
    const chargers = linkedChargerKeys(area);
    const levelRoomKeys = new Set(roomsOnLevel(structure, resolution.levelKey).map(room => room.key));
    return Object.entries(structure?.components || {})
        .filter(([key, component]) => {
            if (!component || component.type !== BaseType.Utility || component.archivedAt) return false;
            if (!component.blueprintLocations?.[imageKey]) return false;
            if (chargers.has(key)) return true;
            return showAll && parentRoomKeysOf(component).some(roomKey => levelRoomKeys.has(roomKey));
        })
        .map(([key, component]) => ({ ...component, key }));
};

export interface ChargerSpotLink {
    area: ParkingArea;
    spot: ParkingSpot;
}

/** Spots in any area linked to the charger, except the excluded (edited) spot. */
export const chargerSpotLinks = (
    areas: ParkingArea[] | Record<string, ParkingArea>,
    componentKey: string,
    exclude?: { areaKey: string; spotKey: string }
): ChargerSpotLink[] => {
    if (!componentKey) return [];
    const list = Array.isArray(areas) ? areas : Object.values(areas);
    const links: ChargerSpotLink[] = [];
    list.forEach(area => {
        Object.entries(area?.spots || {}).forEach(([spotKey, spot]) => {
            if (!spot || spot.chargerComponentKey !== componentKey) return;
            if (exclude && area.key === exclude.areaKey && (spot.key || spotKey) === exclude.spotKey) return;
            links.push({ area, spot });
        });
    });
    return links;
};

/**
 * "Ej kopplad", or e.g. "Kopplad till plats 12, 14, plats 3 (Garage B)".
 * Spots in the current area come first without an area name; other areas
 * follow by name. Numbers are sorted naturally.
 */
export const chargerSpotLinkLabel = (links: ChargerSpotLink[], currentAreaKey?: string): string => {
    if (links.length === 0) return 'Ej kopplad';

    const groups = new Map<string, ChargerSpotLink[]>();
    links.forEach(link => {
        const group = groups.get(link.area.key);
        if (group) group.push(link);
        else groups.set(link.area.key, [link]);
    });

    const areaName = (area: ParkingArea) => parkingAreaDisplayName(area);
    const parts = Array.from(groups.values())
        .sort((a, b) => {
            const aCurrent = a[0].area.key === currentAreaKey ? 0 : 1;
            const bCurrent = b[0].area.key === currentAreaKey ? 0 : 1;
            if (aCurrent !== bCurrent) return aCurrent - bCurrent;
            const byName = naturalCompare(areaName(a[0].area), areaName(b[0].area));
            return byName !== 0 ? byName : a[0].area.key.localeCompare(b[0].area.key);
        })
        .map(group => {
            const numbers = group
                .map(link => (link.spot.number || '').trim())
                .filter(n => n.length > 0)
                .sort(naturalCompare);
            const segments: string[] = [];
            if (numbers.length > 0) segments.push(`plats ${numbers.join(', ')}`);
            if (numbers.length < group.length) segments.push('plats utan nummer');
            const text = segments.join(', ');
            const area = group[0].area;
            return area.key === currentAreaKey ? text : `${text} (${areaName(area)})`;
        });

    return `Kopplad till ${parts.join(', ')}`;
};
